import React, { useEffect, useMemo, useState } from "react";
import routeMailing from "./routes";
import { useSelector } from "react-redux";
import { routeAuth } from "../AuthPage";
import { Redirect } from "react-router-dom";
import dayjs from "dayjs";

import "./styles.scss";

import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Progress,
    Row,
    Segmented,
    Space,
    Statistic,
    Table,
    Tag,
    Timeline,
    Tooltip,
    Typography,
    notification,
} from "antd";

import {
    BarChartOutlined,
    CalendarOutlined,
    CloseOutlined,
    DatabaseOutlined,
    ReloadOutlined,
    RocketOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { UserHeader } from "../../components/UserHeader/UserHeader";
import { getRequest, postRequest } from "../../services/requestController";

const { Title, Text } = Typography;

const toShortText = (v, max = 120) => {
    const s = typeof v === "string" ? v : "";
    if (s.length <= max) return s;
    return `${s.slice(0, max).trim()}…`;
};

const formatTs = (v) => {
    if (!v) return "—";
    const d = dayjs(v);
    if (!d.isValid()) return "—";
    return d.format("DD.MM.YYYY HH:mm");
};

const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const MailingPage = () => {
    const token = useSelector((state) => state.token);

    const [loadingSenders, setLoadingSenders] = useState(false);
    const [senders, setSenders] = useState([]);

    const [composeOpen, setComposeOpen] = useState(false);
    const [composeLoading, setComposeLoading] = useState(false);
    const [composeMode, setComposeMode] = useState("broadcast");

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsSender, setDetailsSender] = useState(null);

    const [period, setPeriod] = useState("7d");

    const [form] = Form.useForm();

    const fetchSenders = async () => {
        setLoadingSenders(true);
        try {
            const res = await getRequest("/api/admin/sender", token);
            if (!res.success) {
                notification.error({
                    message: res.error?.message || "Ошибка",
                    description: (res.error?.description || []).join("\n"),
                });
                return;
            }
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            setSenders(list);
        } finally {
            setLoadingSenders(false);
        }
    };

    useEffect(() => {
        if (!token) return;
        fetchSenders();
    }, [token]);

    const normalized = useMemo(() => {
        const list = Array.isArray(senders) ? senders : [];
        return list
            .map((s) => ({
                senderId: safeNum(s?.senderId),
                title: typeof s?.title === "string" ? s.title : "",
                description: typeof s?.description === "string" ? s.description : "",
                userIds: Array.isArray(s?.userIds) ? s.userIds : [],
                createdAt: s?.createdAt || s?.updatedAt || null,
                updatedAt: s?.updatedAt || null,
            }))
            .filter((s) => s.senderId > 0);
    }, [senders]);

    const totals = useMemo(() => {
        const total = normalized.length;
        const sorted = [...normalized].sort((a, b) => (b.senderId || 0) - (a.senderId || 0));
        const last = sorted[0] || null;

        const totalRecipients = normalized.reduce((sum, s) => sum + (Array.isArray(s.userIds) ? s.userIds.length : 0), 0);
        const avgRecipients = total ? Math.round(totalRecipients / total) : 0;

        const titled = normalized.filter((s) => String(s.title || "").trim()).length;
        const titledShare = total ? Math.round((titled / total) * 100) : 0;

        const maxRecipients = normalized.reduce((m, s) => Math.max(m, Array.isArray(s.userIds) ? s.userIds.length : 0), 0);

        const today = dayjs().startOf("day");
        const sentToday = normalized.filter((s) => (s.createdAt ? dayjs(s.createdAt).isSame(today, "day") : false)).length;

        return { total, last, totalRecipients, avgRecipients, titled, titledShare, maxRecipients, sentToday };
    }, [normalized]);

    const periodFiltered = useMemo(() => {
        const now = dayjs();
        const from =
            period === "24h"
                ? now.subtract(24, "hour")
                : period === "7d"
                    ? now.subtract(7, "day")
                    : period === "30d"
                        ? now.subtract(30, "day")
                        : now.subtract(365, "day");

        const list = normalized.filter((s) => (s.createdAt ? dayjs(s.createdAt).isAfter(from) : false));
        return { from, list };
    }, [normalized, period]);

    const periodStats = useMemo(() => {
        const list = periodFiltered.list;
        const count = list.length;
        const recipients = list.reduce((sum, s) => sum + (Array.isArray(s.userIds) ? s.userIds.length : 0), 0);
        const avg = count ? Math.round(recipients / count) : 0;

        const titled = list.filter((s) => String(s.title || "").trim()).length;
        const titledPct = count ? Math.round((titled / count) * 100) : 0;

        const top = [...list]
            .sort((a, b) => (Array.isArray(b.userIds) ? b.userIds.length : 0) - (Array.isArray(a.userIds) ? a.userIds.length : 0))
            .slice(0, 3);

        const hours = new Array(24).fill(0);
        list.forEach((s) => {
            if (!s.createdAt) return;
            const d = dayjs(s.createdAt);
            if (!d.isValid()) return;
            hours[d.hour()] += 1;
        });
        const peakHour = hours.reduce(
            (best, v, idx) => {
                if (v > best.value) return { hour: idx, value: v };
                return best;
            },
            { hour: 0, value: 0 }
        );

        const distribution = list.reduce(
            (acc, s) => {
                const n = Array.isArray(s.userIds) ? s.userIds.length : 0;
                if (n <= 1) acc.single += 1;
                else if (n <= 100) acc.small += 1;
                else if (n <= 1000) acc.medium += 1;
                else acc.large += 1;
                return acc;
            },
            { single: 0, small: 0, medium: 0, large: 0 }
        );

        return { count, recipients, avg, titled, titledPct, top, peakHour, distribution };
    }, [periodFiltered]);

    const health = useMemo(() => {
        const total = periodStats.count;
        const d = periodStats.distribution;

        const broadcastLike = d.small + d.medium + d.large;
        const broadcastShare = total ? Math.round((broadcastLike / total) * 100) : 0;

        const varianceHint = (() => {
            const values = periodFiltered.list.map((s) => (Array.isArray(s.userIds) ? s.userIds.length : 0));
            if (!values.length) return 0;
            const mean = values.reduce((a, b) => a + b, 0) / values.length;
            const v = values.reduce((a, x) => a + Math.pow(x - mean, 2), 0) / values.length;
            const sd = Math.sqrt(v);
            if (!Number.isFinite(sd) || !Number.isFinite(mean) || mean === 0) return 0;
            return Math.max(0, Math.min(100, Math.round((sd / mean) * 100)));
        })();

        const delayMs = safeNum(form.getFieldValue("delayMs") ?? 120);
        const delayRisk = delayMs < 80 ? 75 : delayMs < 120 ? 45 : delayMs < 220 ? 25 : 10;

        const overallRisk = Math.max(delayRisk, varianceHint, broadcastShare > 80 ? 55 : 20);
        const score = Math.max(0, 100 - overallRisk);

        return { broadcastShare, varianceHint, delayRisk, score };
    }, [periodStats, periodFiltered.list, form, composeMode]);

    const columns = useMemo(
        () => [
            {
                title: "ID",
                dataIndex: "senderId",
                key: "senderId",
                width: 90,
                sorter: (a, b) => (safeNum(a.senderId) || 0) - (safeNum(b.senderId) || 0),
                render: (v) => <Text strong>#{v}</Text>,
            },
            {
                title: "Заголовок",
                dataIndex: "title",
                key: "title",
                width: 240,
                render: (v) => {
                    const s = String(v || "").trim();
                    if (!s) return <Text type="secondary">—</Text>;
                    return <Text>{toShortText(s, 60)}</Text>;
                },
            },
            {
                title: "Текст",
                dataIndex: "description",
                key: "description",
                render: (v) => <Text>{toShortText(String(v || ""), 160)}</Text>,
            },
            {
                title: "Получатели",
                dataIndex: "userIds",
                key: "userIds",
                width: 160,
                sorter: (a, b) => (Array.isArray(a.userIds) ? a.userIds.length : 0) - (Array.isArray(b.userIds) ? b.userIds.length : 0),
                render: (arr) => {
                    const n = Array.isArray(arr) ? arr.length : 0;
                    const color = n <= 1 ? "default" : n <= 100 ? "green" : n <= 1000 ? "blue" : "volcano";
                    return <Tag color={color}>{n}</Tag>;
                },
            },
            {
                title: "Создано",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 170,
                sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
                render: (v) => <Text type="secondary">{formatTs(v)}</Text>,
            },
            {
                title: "Действия",
                key: "actions",
                width: 140,
                render: (_, record) => (
                    <Space>
                        <Button size="small" onClick={() => openDetails(record)}>
                            Детали
                        </Button>
                    </Space>
                ),
            },
        ],
        [normalized]
    );

    const openCompose = () => {
        setComposeOpen(true);
        form.resetFields();
        form.setFieldsValue({ delayMs: 120 });
    };

    const closeCompose = () => {
        setComposeOpen(false);
        setComposeLoading(false);
        form.resetFields();
    };

    const openDetails = (sender) => {
        setDetailsSender(sender || null);
        setDetailsOpen(true);
    };

    const closeDetails = () => {
        setDetailsOpen(false);
        setDetailsSender(null);
    };

    const submitCompose = async () => {
        try {
            const values = await form.validateFields();

            const title = typeof values?.title === "string" ? values.title.trim() : "";
            const description = typeof values?.description === "string" ? values.description.trim() : "";
            const delayMs = Number(values?.delayMs);
            const userId = Number(values?.userId);

            if (!description) {
                form.setFields([{ name: "description", errors: ["Введите текст сообщения"] }]);
                return;
            }

            if (!Number.isFinite(delayMs) || delayMs < 30) {
                form.setFields([{ name: "delayMs", errors: ["delayMs должен быть числом >= 30"] }]);
                return;
            }

            if (composeMode === "user") {
                if (!Number.isFinite(userId) || userId <= 0) {
                    form.setFields([{ name: "userId", errors: ["Укажите userId"] }]);
                    return;
                }
            }

            setComposeLoading(true);

            const body = { title: title || undefined, description, delayMs };
            const url = composeMode === "broadcast" ? "/api/admin/sender/broadcast" : `/api/admin/sender/user/${userId}`;
            const res = await postRequest(url, body, token);

            if (!res.success) {
                notification.error({
                    message: res.error?.message || "Ошибка отправки",
                    description: (res.error?.description || []).join("\n"),
                });
                return;
            }

            if (composeMode === "broadcast") {
                const total = safeNum(res.data?.total);
                const success = safeNum(res.data?.success);
                const failed = safeNum(res.data?.failed);
                notification.success({
                    message: "Рассылка выполнена",
                    description: `Всего: ${total}, доставлено: ${success}, ошибок: ${failed}`,
                });
            } else {
                notification.success({
                    message: "Сообщение отправлено",
                    description: `Пользователь: ${userId}`,
                });
            }

            closeCompose();
            await fetchSenders();
        } finally {
            setComposeLoading(false);
        }
    };

    const topBiggest = useMemo(() => {
        return [...normalized]
            .sort((a, b) => (Array.isArray(b.userIds) ? b.userIds.length : 0) - (Array.isArray(a.userIds) ? a.userIds.length : 0))
            .slice(0, 5);
    }, [normalized]);

    const timelineItems = useMemo(() => {
        const items = [...normalized]
            .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
            .slice(0, 8)
            .map((s) => {
                const recipients = Array.isArray(s.userIds) ? s.userIds.length : 0;
                const color = recipients <= 1 ? "gray" : recipients <= 100 ? "green" : recipients <= 1000 ? "blue" : "red";
                const label = String(s.title || "").trim() ? s.title.trim() : toShortText(s.description, 60);
                return {
                    color,
                    children: (
                        <Space direction="vertical" size={2}>
                            <Space size={8} align="center">
                                <Text strong>#{s.senderId}</Text>
                                <Tag color={recipients <= 1 ? "default" : recipients <= 100 ? "green" : recipients <= 1000 ? "blue" : "volcano"}>
                                    {recipients}
                                </Tag>
                                <Text type="secondary">{formatTs(s.createdAt)}</Text>
                            </Space>
                            <Text>{label || "—"}</Text>
                        </Space>
                    ),
                };
            });

        return items.length ? items : [{ color: "gray", children: <Text type="secondary">Пока нет отправок</Text> }];
    }, [normalized]);

    const composition = useMemo(() => {
        const total = periodStats.count || 0;
        const d = periodStats.distribution;
        const toPct = (n) => (total ? Math.round((n / total) * 100) : 0);

        const singlePct = toPct(d.single);
        const smallPct = toPct(d.small);
        const mediumPct = toPct(d.medium);
        const largePct = toPct(d.large);

        return { total, singlePct, smallPct, mediumPct, largePct };
    }, [periodStats]);

    if (!token) return <Redirect to={routeAuth()} />;

    return (
        <>
            <div className="container mailing-page">
                <UserHeader />

                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={14}>
                        <Space direction="vertical" size={4}>
                            <Title level={3} style={{ marginBottom: 0 }}>
                                Рассылки
                            </Title>
                            <Text type="secondary">
                                Управляй отправками аккуратно: темп, объём, история и “пульс” активности.
                            </Text>
                        </Space>
                    </Col>
                    <Col xs={24} md={10} style={{ textAlign: "right" }}>
                        <Space wrap>
                            <Segmented
                                value={period}
                                onChange={setPeriod}
                                options={[
                                    { label: "24ч", value: "24h", icon: <CalendarOutlined /> },
                                    { label: "7д", value: "7d" },
                                    { label: "30д", value: "30d" },
                                    { label: "365д", value: "365d" },
                                ]}
                            />
                            <Button icon={<ReloadOutlined />} onClick={fetchSenders} loading={loadingSenders}>
                                Обновить
                            </Button>
                            <Button type="primary" icon={<SendOutlined />} onClick={openCompose}>
                                Новая отправка
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Divider />

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={6}>
                        <Card bordered={false} className="mailing-card mailing-card--accent">
                            <Statistic title="Всего рассылок" value={totals.total} />
                            <Text type="secondary">Вся история отправок</Text>
                        </Card>
                    </Col>

                    <Col xs={24} md={6}>
                        <Card bordered={false} className="mailing-card">
                            <Statistic title="Сегодня" value={totals.sentToday} prefix={<RocketOutlined />} />
                            <Text type="secondary">Сколько отправок создано сегодня</Text>
                        </Card>
                    </Col>

                    <Col xs={24} md={6}>
                        <Card bordered={false} className="mailing-card">
                            <Statistic title="Суммарно получателей" value={totals.totalRecipients} prefix={<DatabaseOutlined />} />
                            <Text type="secondary">Сумма userIds по всем отправкам</Text>
                        </Card>
                    </Col>

                    <Col xs={24} md={6}>
                        <Card bordered={false} className="mailing-card">
                            <Statistic title="Средний охват" value={totals.avgRecipients} suffix="польз." />
                            <Text type="secondary">Среднее число получателей</Text>
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} md={8}>
                        <Card bordered={false} className="mailing-card" title="Пульс за выбранный период" extra={<BarChartOutlined />}>
                            <Row gutter={[12, 12]}>
                                <Col span={12}>
                                    <Card bordered={false} className="mailing-mini">
                                        <Statistic title="Отправок" value={periodStats.count} />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card bordered={false} className="mailing-mini">
                                        <Statistic title="Получателей" value={periodStats.recipients} />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card bordered={false} className="mailing-mini">
                                        <Statistic title="Средний охват" value={periodStats.avg} />
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card bordered={false} className="mailing-mini">
                                        <Statistic
                                            title="Пик по времени"
                                            value={periodStats.peakHour.value ? `${String(periodStats.peakHour.hour).padStart(2, "0")}:00` : "—"}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Divider style={{ margin: "12px 0" }} />

                            <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                    <Text type="secondary">Сообщения с заголовком</Text>
                                    <Text strong>{periodStats.titledPct}%</Text>
                                </Space>
                                <Progress percent={periodStats.titledPct} strokeColor="#722ed1" showInfo={false} />
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card bordered={false} className="mailing-card" title="Профиль объёма" extra={<ThunderboltOutlined />}>
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                    <Text type="secondary">Точечные (≤1)</Text>
                                    <Text strong>{composition.singlePct}%</Text>
                                </Space>
                                <Progress percent={composition.singlePct} strokeColor="#8c8c8c" showInfo={false} />

                                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                    <Text type="secondary">Малые (2–100)</Text>
                                    <Text strong>{composition.smallPct}%</Text>
                                </Space>
                                <Progress percent={composition.smallPct} strokeColor="#52c41a" showInfo={false} />

                                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                    <Text type="secondary">Средние (101–1000)</Text>
                                    <Text strong>{composition.mediumPct}%</Text>
                                </Space>
                                <Progress percent={composition.mediumPct} strokeColor="#1677ff" showInfo={false} />

                                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                    <Text type="secondary">Крупные (&gt;1000)</Text>
                                    <Text strong>{composition.largePct}%</Text>
                                </Space>
                                <Progress percent={composition.largePct} strokeColor="#fa541c" showInfo={false} />
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card bordered={false} className="mailing-card" title="Индекс аккуратности" extra={<SafetyCertificateOutlined />}>
                            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                <Card bordered={false} className="mailing-mini mailing-mini--soft">
                                    <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                        <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                            <Text type="secondary">Score</Text>
                                            <Text strong>{health.score}/100</Text>
                                        </Space>
                                        <Progress
                                            percent={health.score}
                                            strokeColor={health.score >= 75 ? "#52c41a" : health.score >= 50 ? "#faad14" : "#ff4d4f"}
                                        />
                                    </Space>
                                </Card>

                                <Alert
                                    showIcon
                                    type={health.score >= 75 ? "success" : health.score >= 50 ? "warning" : "error"}
                                    message={health.score >= 75 ? "Темп выглядит ок" : health.score >= 50 ? "Нужно аккуратнее" : "Риск высокий"}
                                    description={
                                        <Space direction="vertical" size={4}>
                                            <Text type="secondary">
                                                Доля массовых отправок: <Text strong>{health.broadcastShare}%</Text>
                                            </Text>
                                            <Text type="secondary">
                                                Разброс объёма: <Text strong>{health.varianceHint}%</Text>
                                            </Text>
                                            <Text type="secondary">
                                                Риск по задержке (в модалке): <Text strong>{health.delayRisk}%</Text>
                                            </Text>
                                        </Space>
                                    }
                                />
                            </Space>
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} md={12}>
                        <Card bordered={false} className="mailing-card" title="Последние отправки (лента)">
                            <Timeline items={timelineItems} />
                        </Card>
                    </Col>

                    <Col xs={24} md={12}>
                        <Card bordered={false} className="mailing-card" title="Топ по охвату">
                            {topBiggest.length ? (
                                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                    {topBiggest.map((s) => {
                                        const n = Array.isArray(s.userIds) ? s.userIds.length : 0;
                                        const label = String(s.title || "").trim() ? s.title.trim() : toShortText(s.description, 70);
                                        const pct = totals.maxRecipients ? Math.round((n / totals.maxRecipients) * 100) : 0;

                                        return (
                                            <Card key={s.senderId} bordered={false} className="mailing-mini">
                                                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                    <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                                                        <Space size={10} align="center">
                                                            <Text strong>#{s.senderId}</Text>
                                                            <Tag color={n <= 1 ? "default" : n <= 100 ? "green" : n <= 1000 ? "blue" : "volcano"}>
                                                                {n}
                                                            </Tag>
                                                        </Space>
                                                        <Text type="secondary">{formatTs(s.createdAt)}</Text>
                                                    </Space>
                                                    <Text>{label || "—"}</Text>
                                                    <Progress percent={pct} showInfo={false} strokeColor="#1677ff" />
                                                </Space>
                                            </Card>
                                        );
                                    })}
                                </Space>
                            ) : (
                                <Empty description="Нет данных для топа" />
                            )}
                        </Card>
                    </Col>
                </Row>

                <Divider />

                <Card
                    bordered={false}
                    className="mailing-card"
                    title="Отправленные рассылки"
                    extra={
                        <Space>
                            <Tooltip title="Доля рассылок с заполненным заголовком">
                                <Tag color="purple">{totals.titledShare}% с заголовком</Tag>
                            </Tooltip>
                            <Text type="secondary">{loadingSenders ? "Загрузка…" : `Записей: ${normalized.length}`}</Text>
                        </Space>
                    }
                >
                    <Table
                        loading={loadingSenders}
                        columns={columns}
                        dataSource={normalized}
                        rowKey={(r) => String(r.senderId)}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (t) => `Всего: ${t}`,
                        }}
                    />
                </Card>
            </div>

            <Modal
                open={composeOpen}
                onCancel={closeCompose}
                title="Новая отправка"
                okText={composeMode === "broadcast" ? "Запустить рассылку" : "Отправить"}
                cancelText="Отмена"
                onOk={submitCompose}
                confirmLoading={composeLoading}
                destroyOnClose
            >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Card bordered={false} className="mailing-mini mailing-mini--soft">
                        <Space direction="vertical" size={10} style={{ width: "100%" }}>
                            <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
                                <Space size={8} align="center">
                                    <Tag color={composeMode === "broadcast" ? "blue" : "green"}>
                                        {composeMode === "broadcast" ? "РАССЫЛКА" : "ОДНОМУ ПОЛЬЗОВАТЕЛЮ"}
                                    </Tag>
                                    <Text type="secondary">Выберите режим отправки</Text>
                                </Space>
                                <Space size={8} align="center">
                                    <Text type="secondary">Score</Text>
                                    <Text strong>{health.score}/100</Text>
                                </Space>
                            </Space>

                            <Progress
                                percent={health.score}
                                showInfo={false}
                                strokeColor={health.score >= 75 ? "#52c41a" : health.score >= 50 ? "#faad14" : "#ff4d4f"}
                            />

                            <Segmented
                                block
                                value={composeMode}
                                onChange={setComposeMode}
                                options={[
                                    { label: "Всем активным", value: "broadcast", icon: <SendOutlined /> },
                                    { label: "Одному", value: "user", icon: <UserOutlined /> },
                                ]}
                            />
                        </Space>
                    </Card>

                    <Form form={form} layout="vertical">
                        {composeMode === "user" ? (
                            <Form.Item
                                label="userId пользователя"
                                name="userId"
                                rules={[{ required: true, message: "Укажите userId" }]}
                            >
                                <Input placeholder="Например: 123456789" inputMode="numeric" />
                            </Form.Item>
                        ) : null}

                        <Form.Item label="Заголовок (необязательно)" name="title">
                            <Input placeholder="Например: Новые бонусы" maxLength={120} />
                        </Form.Item>

                        <Form.Item
                            label="Текст сообщения"
                            name="description"
                            rules={[{ required: true, message: "Введите текст сообщения" }]}
                        >
                            <Input.TextArea placeholder="Текст рассылки" autoSize={{ minRows: 5, maxRows: 10 }} maxLength={4000} />
                        </Form.Item>

                        <Form.Item
                            label="Задержка между сообщениями (ms)"
                            name="delayMs"
                            rules={[{ required: true, message: "Укажите delayMs" }]}
                        >
                            <InputNumber min={30} step={10} style={{ width: "100%" }} placeholder="120" />
                        </Form.Item>
                    </Form>

                    <Alert
                        showIcon
                        type="info"
                        message="Подсказка"
                        description="HTML разметка поддерживается. Слишком маленькая задержка увеличивает риск ограничений со стороны Telegram."
                    />
                </Space>
            </Modal>

            <Drawer
                open={detailsOpen}
                onClose={closeDetails}
                title={detailsSender ? `Рассылка #${detailsSender.senderId}` : "Детали рассылки"}
                width={560}
                destroyOnClose
                extra={
                    <Button icon={<CloseOutlined />} onClick={closeDetails}>
                        Закрыть
                    </Button>
                }
            >
                {detailsSender ? (
                    <Space direction="vertical" size={14} style={{ width: "100%" }}>
                        <Row gutter={[12, 12]}>
                            <Col span={12}>
                                <Card bordered={false} className="mailing-mini">
                                    <Statistic title="senderId" value={detailsSender.senderId} />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card bordered={false} className="mailing-mini">
                                    <Statistic title="Получатели" value={Array.isArray(detailsSender.userIds) ? detailsSender.userIds.length : 0} />
                                </Card>
                            </Col>
                        </Row>

                        <Card bordered={false} className="mailing-mini">
                            <Descriptions
                                size="small"
                                column={1}
                                items={[
                                    { key: "createdAt", label: "Создано", children: formatTs(detailsSender.createdAt) },
                                    { key: "updatedAt", label: "Обновлено", children: formatTs(detailsSender.updatedAt) },
                                    {
                                        key: "title",
                                        label: "Заголовок",
                                        children: String(detailsSender.title || "").trim() || "—",
                                    },
                                ]}
                            />
                        </Card>

                        <Card bordered={false} className="mailing-mini" title="Текст">
                            <Text style={{ whiteSpace: "pre-wrap" }}>{String(detailsSender.description || "")}</Text>
                        </Card>

                        <Card bordered={false} className="mailing-mini" title="userIds (первые 80)">
                            <Text type="secondary">
                                {Array.isArray(detailsSender.userIds) && detailsSender.userIds.length
                                    ? detailsSender.userIds.slice(0, 80).join(", ")
                                    : "—"}
                            </Text>
                        </Card>
                    </Space>
                ) : null}
            </Drawer>
        </>
    );
};

export { routeMailing };

export default MailingPage;